// Copyright (c) 2021, 37.78 Tecnologia Ltda. All rights reserved.

import { getEmail, getFhirId } from "../../services/firebase";
import errorHandler from "../../util/errorHandler";
import { clearedAttributes } from "./utils";

/**
 * Modified from
 * https://developers.intercom.com/installing-intercom/docs/basic-javascript#section-basic-javascript
 */
/* eslint-disable prefer-rest-params */
const addIntercom = () => {
  const APP_ID = process.env.REACT_APP_INTERCOM_APP_ID;
  const w = window;
  const ic = w.Intercom;
  if (typeof ic === "function") {
    ic("reattach_activator");
    ic("update", w.intercomSettings);
  } else {
    const d = document;
    const i = function () {
      i.c(arguments);
    };
    i.q = [];
    i.c = function (args) {
      i.q.push(args);
    };
    w.Intercom = i;
    const l = function () {
      const s = d.createElement("script");
      s.type = "text/javascript";
      s.async = true;
      s.src = `https://widget.intercom.io/widget/${APP_ID}`;
      const x = d.getElementsByTagName("script")[0];
      x.parentNode.insertBefore(s, x);
    };
    if (document.readyState === "complete") {
      l();
    } else if (w.attachEvent) {
      w.attachEvent("onload", l);
    } else {
      w.addEventListener("load", l, false);
    }
  }
};
/* eslint-enable prefer-rest-params */

const shutdownIntercom = () => window.Intercom("shutdown");

/**
 * Intercom does not provide a way to clear "onHide" callbacks, so
 * we added onHideCallback to ensure only one callback is set
 */
let onHideCallback = null;

const onHideIntercom = () => {
  if (onHideCallback) {
    onHideCallback();
  }
};

const setOnHide = (callback) => {
  onHideCallback = callback;
  return () => {
    onHideCallback = null;
  };
};

const getUserData = async (user) => {
  if (!user) return {};

  const userEmail = getEmail();
  const userId = await getFhirId(user);

  if (!userId) {
    errorHandler.report(new Error("Can't get userId from token"));
    return;
  }

  return {
    campaign: process.env.REACT_APP_INTERCOM_CAMPAIGN_URL,
    email: userEmail,
    user_id: userId,
    // Attributes to clear
    ...clearedAttributes,
  };
};

const bootIntercom = async (currentUser) => {
  if (!currentUser) return;

  const userData = await getUserData(currentUser);

  window.Intercom("boot", {
    app_id: process.env.REACT_APP_INTERCOM_APP_ID,
    hide_default_launcher: true,
    ...userData,
  });
  window.Intercom("onHide", onHideIntercom);
};

const openIntercom = async () => {
  window.setTimeout(() => {
    window.Intercom("showNewMessage");
  }, 300);
};

const getDataToUpdate = (patient, user_email, user_id) => ({
  // User data
  user_id,
  email: user_email,
  cpf: patient?.cpf,
  name: patient?.fullName,
  "first name": patient?.name,
  phone: patient?.phone,
  cep: patient?.cep,
  "Cidade e Estado": `${patient?.city || ""}, ${patient?.state || ""}`,
  "Data de Nascimento": patient?.birthdate,
  company: {
    company_id: patient?.organizationId,
    name: patient?.organization,
  },
  idade: patient?.age,
  sexo: patient?.gender === "male" ? "masculino" : "feminino",
  cidade: patient?.city,
  estado: patient?.state,
  data_de_nascimento: patient?.birthdate,
  campaign: process.env.REACT_APP_INTERCOM_CAMPAIGN_URL,

  // Attributes to clear
  ...clearedAttributes,
});

const updateIntercomUser = async ({ currentUser, patient }) => {
  if (!currentUser) return;

  const userEmail = await getEmail();
  const userId = await getFhirId(currentUser);

  if (!userId) {
    errorHandler.report(new Error("Can't get userId from token"));
    return;
  }

  const data = getDataToUpdate(patient, userEmail, userId);
  window.Intercom("update", data);
};

export {
  addIntercom,
  bootIntercom,
  openIntercom,
  shutdownIntercom,
  updateIntercomUser,
  setOnHide,
};
